import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");

  const [state, setState] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          setTimeout(() => navigate(`/booking/${bookingId}/sign`), 1500);
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        if (n >= 15) { setState("error"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch { setState("error"); }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, bookingId, navigate]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#0B0C0E] animate-spin mx-auto" />
            <h1 className="mt-8 font-display font-black text-3xl uppercase tracking-tight text-[#0B0C0E]">Confirming payment…</h1>
            <p className="mt-3 text-[#56606E]">Please don&apos;t close this window. Attempt {attempts + 1} of 15.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
            <h1 className="mt-8 font-display font-black text-4xl uppercase tracking-tight text-[#0B0C0E]">Payment Received</h1>
            <p className="mt-3 text-[#56606E]">Redirecting you to sign your lease…</p>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="w-16 h-16 text-amber-600 mx-auto" />
            <h1 className="mt-8 font-display font-black text-3xl uppercase tracking-tight text-[#0B0C0E]">Session Expired</h1>
            <p className="mt-3 text-[#56606E]">Please start the booking again.</p>
            <button onClick={() => navigate("/")} className="mt-8 bg-[#0B0C0E] text-white font-bold px-8 py-3 uppercase border-l-4 border-[#FACC15]">Back to home</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto" />
            <h1 className="mt-8 font-display font-black text-3xl uppercase tracking-tight text-[#0B0C0E]">Could not verify payment</h1>
            <p className="mt-3 text-[#56606E]">If you were charged, contact lease@14acre.ca with reference {sessionId?.slice(0, 12)}…</p>
            <button onClick={() => navigate("/")} className="mt-8 bg-[#0B0C0E] text-white font-bold px-8 py-3 uppercase border-l-4 border-[#FACC15]">Back to home</button>
          </>
        )}
      </div>
    </div>
  );
}
